import { executionSteps, executionTakeaways } from '../data';

export default function ExecutionModelsSection() {
  return (
    <section className="relative w-full bg-background-100 overflow-hidden">
      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-12 md:py-18">

        {/* ── Editorial masthead ── */}
        <div className="mb-10 md:mb-14 max-w-[760px]">
          <h2 className="text-[32px] md:text-[46px] font-[750] leading-[1.08] text-foreground-950 tracking-[-0.02em]">
            Same trade, completely different plumbing.
          </h2>
          <p className="mt-6 text-[15px] text-foreground-600 leading-relaxed max-w-[560px]">
            The pricing models aren&rsquo;t just different line items &mdash; they reflect fundamentally different business relationships between you and your broker.
          </p>
        </div>

        {/* ── The journey: origin → branches → destination ── */}
        <div className="relative">

          {/* Desktop vertical spine */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-300/50 -translate-x-1/2 hidden lg:block" />

          {/* Origin: the order */}
          <div className="relative flex justify-center mb-12 lg:mb-16">
            <div className="relative z-10 inline-flex px-5 py-3 rounded-xl border border-background-300/60 bg-background-50">
              <span className="text-[13px] font-semibold text-foreground-800 tracking-wide">
                Buy EURUSD &mdash; 1 standard lot
              </span>
            </div>
            <div className="hidden lg:block absolute top-full left-1/2 -translate-x-1/2 w-px h-8 bg-background-300/50" />
          </div>

          {/* Steps */}
          <div className="space-y-12 lg:space-y-0">
            {executionSteps.map((step, i) => (
              <div key={i} className="relative lg:grid lg:grid-cols-[1fr_72px_1fr] lg:gap-6 lg:items-start">

                {/* ── Mobile only: step header ── */}
                <div className="lg:hidden flex items-center gap-4 mb-6">
                  <span className="w-11 h-11 rounded-full bg-background-50 border border-background-300/60 flex items-center justify-center text-[12px] font-bold text-foreground-700">
                    {step.step}
                  </span>
                  <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-foreground-500">
                    {step.label}
                  </span>
                </div>

                {/* ── Left branch: Spread-Only ── */}
                <div className="lg:text-right lg:pr-2 lg:pt-6 lg:pb-12 relative">
                  <div className="lg:hidden pl-5 border-l-2 border-primary-300/50 mb-5">
                    <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary-600 block mb-1.5">
                      Spread-Only
                    </span>
                    <p className="text-[14px] text-foreground-600 leading-[1.7]">
                      {step.spreadOnly}
                    </p>
                  </div>

                  <div className="hidden lg:block">
                    <div className="absolute top-[42px] right-0 w-full h-px bg-primary-300/30" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary-600 block mb-2">
                      Spread-Only
                    </span>
                    <p className="text-[15px] text-foreground-600 leading-[1.7] max-w-[380px] ml-auto">
                      {step.spreadOnly}
                    </p>
                  </div>
                </div>

                {/* ── Center node ── */}
                <div className="relative hidden lg:flex flex-col items-center pt-2 pb-12">
                  <div className="relative z-10 w-[52px] h-[52px] rounded-full bg-background-50 border border-background-300/60 flex flex-col items-center justify-center">
                    <span className="text-[11px] font-bold text-foreground-500">{step.step}</span>
                    <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-foreground-400 mt-[-2px]">/04</span>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-foreground-400 mt-3 text-center max-w-[80px]">
                    {step.label}
                  </span>
                  <div className="absolute top-[26px] left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-foreground-300" />
                </div>

                {/* ── Right branch: ECN ── */}
                <div className="lg:pl-2 lg:pt-6 lg:pb-12 relative">
                  <div className="lg:hidden pl-5 border-l-2 border-accent-300/50">
                    <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-accent-600 block mb-1.5">
                      ECN
                    </span>
                    <p className="text-[14px] text-foreground-600 leading-[1.7]">
                      {step.ecn}
                    </p>
                  </div>

                  <div className="hidden lg:block">
                    <div className="absolute top-[42px] left-0 w-full h-px bg-accent-300/30" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-accent-600 block mb-2">
                      ECN
                    </span>
                    <p className="text-[15px] text-foreground-600 leading-[1.7] max-w-[380px]">
                      {step.ecn}
                    </p>
                  </div>
                </div>

                {i < executionSteps.length - 1 && (
                  <div className="lg:hidden flex justify-center my-2">
                    <div className="w-px h-8 bg-background-300/50" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Destination: two outcomes */}
          <div className="relative mt-4 lg:mt-0">
            <div className="hidden lg:block absolute bottom-full left-1/2 -translate-x-1/2 w-px h-6 bg-background-300/50" />

            <div className="lg:grid lg:grid-cols-2 lg:gap-8">
              {/* Spread-Only outcome */}
              <div className="text-center lg:text-right lg:pr-2 mb-6 lg:mb-0">
                <div className="inline-flex px-5 py-3 rounded-xl border border-primary-200/60 bg-primary-50/60">
                  <div className="text-left lg:text-right">
                    <span className="text-[11px] font-bold uppercase tracking-[0.10em] text-primary-600 block">Spread-Only Total</span>
                    <span className="text-[18px] font-[750] text-foreground-950">$12.00</span>
                    <span className="text-[13px] text-foreground-500 ml-1.5">hidden in the spread</span>
                  </div>
                </div>
              </div>

              {/* ECN outcome */}
              <div className="text-center lg:text-left lg:pl-2">
                <div className="inline-flex px-5 py-3 rounded-xl border border-accent-200/60 bg-accent-50/60">
                  <div className="text-left">
                    <span className="text-[11px] font-bold uppercase tracking-[0.10em] text-accent-600 block">ECN Total</span>
                    <span className="text-[18px] font-[750] text-foreground-950">$9.00</span>
                    <span className="text-[13px] text-foreground-500 ml-1.5">$2 spread + $7 commission</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Takeaways ── */}
        <div className="mt-20 md:mt-28 pt-10 border-t border-background-300/50">
          <div className="flex items-center gap-3 mb-10">
            <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-foreground-500">
              What this means
            </span>
            <span className="h-px flex-1 bg-background-300/50" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {executionTakeaways.map((item, i) => {
              const borders = [
                'border-primary-300/60',
                'border-accent-300/60',
                'border-secondary-300/60',
              ];
              const tags = [
                'text-primary-600 bg-primary-50/60 border-primary-200/60',
                'text-accent-600 bg-accent-50/60 border-accent-200/60',
                'text-secondary-600 bg-secondary-50/60 border-secondary-200/60',
              ];
              return (
                <div key={i} className={`relative pl-5 border-l-[2px] ${borders[i % 3]}`}>
                  <span className={`inline-block text-[10px] font-bold uppercase tracking-[0.10em] px-2 py-0.5 rounded border mb-3 ${tags[i % 3]}`}>
                    {i === 0 ? 'Spread-Only' : i === 1 ? 'ECN' : 'Key Insight'}
                  </span>
                  <h4 className="text-[15px] font-[750] text-foreground-950 mb-2">{item.title}</h4>
                  <p className="text-[14px] text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}